import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import documentsService from "../services/documentsService";
import styles from "./TicketDocumentCreate.module.css";

const TicketDocumentEdit = () => {
  const { id: ticketId, documentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [ticket, setTicket] = useState(null);
  const [document, setDocument] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadInitialData();
  }, [ticketId, documentId]);

  const loadInitialData = async () => {
    try {
      setInitialLoading(true);
      
      // Load ticket and document data
      const [ticketData, documentData] = await Promise.all([
        documentsService.getTicket(ticketId),
        documentsService.getTicketDocument(ticketId, documentId),
      ]);

      setTicket(ticketData);
      setDocument(documentData);
      setFormData({
        name: documentData.name || "",
      });
    } catch (error) {
      console.error("Error loading initial data:", error);
      
      // Fallback to mock data
      setTicket({
        id: ticketId,
        title: "Sample Ticket",
        property: { name: "Sample Property" },
        status: "Open"
      });
      
      setDocument({
        id: documentId,
        name: "Sample Document.pdf",
        date_uploaded: "2024-01-15",
        visible_to_guardian: true,
        type: { display_name: "General Document" }
      });
      
      setFormData({
        name: "Sample Document.pdf",
      });
      
      toast.warning("Using mock data due to API connection issues");
    } finally {
      setInitialLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Document name is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      
      await documentsService.updateTicketDocument(ticketId, documentId, formData);
      
      toast.success("Document updated successfully!");
      navigate(`/tickets/${ticketId}`);
    } catch (error) {
      console.error("Error updating document:", error);
      
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        toast.error("Failed to update document");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(`/tickets/${ticketId}`);
  };

  if (initialLoading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loader}>Loading...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Breadcrumbs */}
      <div className={styles.breadcrumbs}>
        <span 
          className={styles.breadcrumbItem}
          onClick={() => navigate("/tickets")}
        >
          Tickets
        </span>
        <span className={styles.breadcrumbSeparator}>/</span>
        <span 
          className={styles.breadcrumbItem}
          onClick={() => navigate(`/tickets/${ticketId}`)}
        >
          {ticket?.title || `Ticket ${ticketId}`}
        </span>
        <span className={styles.breadcrumbSeparator}>/</span>
        <span className={styles.breadcrumbActive}>Edit Document</span>
      </div>

      <div className={styles.row}>
        <div className={styles.colMd12}>
          <div className={styles.panel}>
            <div className={styles.panelHeading}>
              <h3>Edit ticket document</h3>
            </div>
            <div className={styles.panelBody}>
              <form onSubmit={handleSubmit} className={styles.form}>
                <fieldset className={styles.formHorizontal}>
                  {/* Document Type (Read-only) */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>
                      Document Type
                    </label>
                    <div className={styles.colSm5}>
                      <div className={styles.readOnlyField}>
                        {document?.type?.display_name || "General Document"}
                      </div>
                    </div>
                  </div>

                  {/* Document Name */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>
                      Name
                    </label>
                    <div className={styles.colSm5}>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className={styles.formControl}
                        placeholder="Enter document name"
                      />
                      {errors.name && (
                        <span className={styles.errorText}>{errors.name}</span>
                      )}
                    </div>
                  </div>

                  {/* Current File Info */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>
                      Current File
                    </label>
                    <div className={styles.colSm5}>
                      <div className={styles.currentFileInfo}>
                        <div className={styles.fileName}>{document?.name}</div>
                        <div className={styles.fileDetails}>
                          Uploaded: {document?.date_uploaded ? new Date(document.date_uploaded).toLocaleDateString("en-GB") : "N/A"}
                        </div>
                        <div className={styles.fileDetails}>
                          Guardian Visible: {document?.visible_to_guardian ? "Yes" : "No"}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Form Actions */}
                  <div className={styles.formGroup}>
                    <div className={styles.textRight}>
                      <button
                        type="button"
                        onClick={handleCancel}
                        className={`${styles.btn} ${styles.btnDefault}`}
                        disabled={loading}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className={`${styles.btn} ${styles.btnHead} ${styles.btnStandard}`}
                        disabled={loading}
                      >
                        {loading ? (
                          <span>Saving...</span>
                        ) : (
                          "Save document"
                        )}
                      </button>
                    </div>
                  </div>
                </fieldset>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TicketDocumentEdit;
